import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = "https://backend.reportingbot.in/api/medical-translation-rules/";

export default function MedicalTranslationRules() {
  const [rules, setRules] = useState([]);
  const [english, setEnglish] = useState("");
  const [hindi, setHindi] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch Rules
  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE);
      setRules(res.data.results || res.data);
      setError("");
    } catch (err) {
      console.error("Error fetching rules", err);
      setError("Failed to load translation rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  // Filter rules based on search query
  const filteredRules = rules.filter((rule) => {
    const query = searchQuery.toLowerCase();
    return (
      rule.english_text.toLowerCase().includes(query) ||
      rule.hindi_text.toLowerCase().includes(query)
    );
  });

  // Add or Update Rule
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!english.trim() || !hindi.trim()) {
      setError("Both fields are required");
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (editingId) {
        await axios.put(`${API_BASE}${editingId}/`, {
          english_text: english,
          hindi_text: hindi,
          is_active: true,
        });
      } else {
        await axios.post(API_BASE, {
          english_text: english,
          hindi_text: hindi,
          is_active: true,
        });
      }

      setEnglish("");
      setHindi("");
      setEditingId(null);
      fetchRules();
    } catch (err) {
      console.error("Error saving rule", err);
      setError("Failed to save rule. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete Rule
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this translation rule?")) return;

    try {
      setLoading(true);
      await axios.delete(`${API_BASE}${id}/`);
      fetchRules();
    } catch (err) {
      console.error("Error deleting rule", err);
      setError("Failed to delete rule");
      setLoading(false);
    }
  };

  // Edit Rule
  const handleEdit = (rule) => {
    setEnglish(rule.english_text);
    setHindi(rule.hindi_text);
    setEditingId(rule.id);
    setError("");
  };

  // Cancel Edit
  const handleCancel = () => {
    setEnglish("");
    setHindi("");
    setEditingId(null);
    setError("");
  };

  // Clear Search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Medical Translation Rules</h1>
            <p style={styles.subtitle}>
              Manage English to Hindi translation rules for medical terminology
            </p>
          </div>
          <div style={styles.badge}>
            {rules.length} {rules.length === 1 ? 'Rule' : 'Rules'}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={styles.errorBox}>
            <span style={styles.errorIcon}>⚠</span>
            {error}
          </div>
        )}

        {/* Form Card */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>
            {editingId ? "Edit Translation Rule" : "Add New Translation Rule"}
          </h3>
          
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>English Text / Regex</label>
                <input
                  type="text"
                  placeholder="e.g., diabetes, blood pressure"
                  value={english}
                  onChange={(e) => setEnglish(e.target.value)}
                  style={styles.input}
                  disabled={loading}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Hindi Translation</label>
                <input
                  type="text"
                  placeholder="हिंदी अनुवाद"
                  value={hindi}
                  onChange={(e) => setHindi(e.target.value)}
                  style={styles.input}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={styles.formActions}>
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  style={styles.cancelButton}
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                style={{
                  ...styles.submitButton,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                disabled={loading}
              >
                {loading ? "Saving..." : editingId ? "Update Rule" : "Add Rule"}
              </button>
            </div>
          </form>
        </div>

        {/* Table Card */}
        <div style={styles.card}>
          <div style={styles.tableHeader}>
            <h3 style={styles.cardTitle}>Translation Rules</h3>
            
            {/* Search Bar */}
            <div style={styles.searchContainer}>
              <div style={styles.searchWrapper}>
                <span style={styles.searchIcon}>🔍</span>
                <input
                  type="text"
                  placeholder="Search by English or Hindi text..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={styles.searchInput}
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    style={styles.clearButton}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchQuery && (
                <p style={styles.searchResults}>
                  Found {filteredRules.length} {filteredRules.length === 1 ? 'rule' : 'rules'}
                </p>
              )}
            </div>
          </div>
          
          {loading && rules.length === 0 ? (
            <div style={styles.loadingState}>
              <div style={styles.spinner}></div>
              <p>Loading translation rules...</p>
            </div>
          ) : filteredRules.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>
                {searchQuery ? "🔍" : "📝"}
              </div>
              <h4 style={styles.emptyTitle}>
                {searchQuery ? "No matching rules found" : "No translation rules yet"}
              </h4>
              <p style={styles.emptyText}>
                {searchQuery 
                  ? `No rules match "${searchQuery}". Try a different search term.`
                  : "Add your first translation rule using the form above"
                }
              </p>
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  style={styles.clearSearchButton}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>English</th>
                    <th style={styles.th}>Hindi</th>
                    <th style={{...styles.th, width: '180px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.map((rule) => (
                    <tr key={rule.id} style={styles.tr}>
                      <td style={styles.td}>{rule.english_text}</td>
                      <td style={styles.td}>{rule.hindi_text}</td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button
                            style={styles.editBtn}
                            onClick={() => handleEdit(rule)}
                            disabled={loading}
                          >
                            <span style={styles.btnIcon}>✏️</span>
                            Edit
                          </button>
                          <button
                            style={styles.deleteBtn}
                            onClick={() => handleDelete(rule.id)}
                            disabled={loading}
                          >
                            <span style={styles.btnIcon}>🗑️</span>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "20px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    flexWrap: "wrap",
    gap: "15px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1a202c",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "16px",
    color: "#718096",
    margin: 0,
  },
  badge: {
    backgroundColor: "#4299e1",
    color: "white",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "600",
  },
  errorBox: {
    backgroundColor: "#fff5f5",
    border: "1px solid #fc8181",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "20px",
    color: "#c53030",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  errorIcon: {
    fontSize: "18px",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#2d3748",
    marginTop: 0,
    marginBottom: "20px",
  },
  tableHeader: {
    marginBottom: "20px",
  },
  searchContainer: {
    marginTop: "16px",
  },
  searchWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "16px",
    fontSize: "18px",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "12px 45px 12px 48px",
    backgroundColor: "#f7fafc",
    border: "2px solid #e2e8f0",
    color: "#2d3748",
    borderRadius: "8px",
    fontSize: "15px",
    transition: "all 0.2s",
    outline: "none",
  },
  clearButton: {
    position: "absolute",
    right: "12px",
    backgroundColor: "transparent",
    border: "none",
    color: "#a0aec0",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "all 0.2s",
  },
  searchResults: {
    fontSize: "14px",
    color: "#718096",
    marginTop: "8px",
    marginBottom: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#4a5568",
  },
  input: {
    padding: "12px 16px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    color: "#2d3748",
    borderRadius: "8px",
    fontSize: "15px",
    transition: "all 0.2s",
    outline: "none",
  },
  formActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  submitButton: {
    backgroundColor: "#4299e1",
    color: "#fff",
    border: "none",
    padding: "12px 24px",
    cursor: "pointer",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  cancelButton: {
    backgroundColor: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    padding: "12px 24px",
    cursor: "pointer",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  tableWrapper: {
    overflowX: "auto",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    backgroundColor: "#f7fafc",
    padding: "12px 16px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #e2e8f0",
  },
  tr: {
    borderBottom: "1px solid #e2e8f0",
    transition: "background-color 0.2s",
  },
  td: {
    padding: "16px",
    fontSize: "15px",
    color: "#2d3748",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  editBtn: {
    backgroundColor: "#4299e1",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    cursor: "pointer",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s",
  },
  deleteBtn: {
    backgroundColor: "#f56565",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    cursor: "pointer",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s",
  },
  btnIcon: {
    fontSize: "14px",
  },
  loadingState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#718096",
  },
  spinner: {
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #4299e1",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    animation: "spin 1s linear infinite",
    margin: "0 auto 16px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    margin: "0 0 8px 0",
  },
  emptyText: {
    fontSize: "15px",
    color: "#718096",
    margin: "0 0 16px 0",
  },
  clearSearchButton: {
    backgroundColor: "#4299e1",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    cursor: "pointer",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.2s",
  },
};